import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import type {
  BurndownReport,
  ReportSummary,
  StreamRow,
  TesterRow,
  TrendResponse,
} from '@testflow/shared';
import { Children, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ExecutionStatusCard } from '../../components/reporting/cards/execution-status-card.js';
import { ExitCriteriaCard } from '../../components/reporting/cards/exit-criteria-card.js';
import {
  ForecastCard,
  type ForecastControls,
} from '../../components/reporting/cards/forecast-card.js';
import { SeverityCard } from '../../components/reporting/cards/severity-card.js';
import { SimplificationCoverageCard } from '../../components/reporting/cards/simplification-coverage-card.js';
import { StreamsTableCard } from '../../components/reporting/cards/streams-table-card.js';
import { TestersTableCard } from '../../components/reporting/cards/testers-table-card.js';
import { TrendCard } from '../../components/reporting/cards/trend-card.js';
import { E2eChainCoverageSection } from '../../components/reporting/e2e-chain-coverage.js';
import { KpiCard } from '../../components/reporting/kpi-card.js';
import { digestApi } from '../../lib/digest-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import { defaultActivePlanId, sortActivePlans } from '../../lib/plan-selection.js';
import type { TestPlan } from '../../lib/plans-api.js';
import { plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { reportsApi } from '../../lib/reports-api.js';
import { scopeHintFrom } from '../../lib/scope-hint.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { useUrlStateList } from '../../lib/use-url-state.js';

// #55: Mehrere Testpläne dürfen gleichzeitig aktiv sein — die Seite wählt deshalb nicht mehr
// genau einen Plan, sondern eine Mehrfachauswahl (Kontrollkästchen, aktive Pläne zuerst; Vorbelegung
// via `defaultActivePlanId`, nicht `plans[0]`). Jeder gewählte Plan bekommt einen eigenen Block über
// `PlanReportBlock`, der genau das rendert, was die Seite vor diesem Task gerendert hat — die
// bestehenden Einzel-Plan-Endpunkte (/reports/summary, /reports/burndown, /reports/forecast) bleiben
// unverändert und werden über `useQueries` je gewählter Plan-ID einzeln aufgerufen. Es wird NICHT
// über Pläne summiert: ein Testfall kann in zwei Plänen liegen, eine Summe würde ihn doppelt zählen
// und die Coverage verzerren (Produktentscheidung aus dem Task-7-Briefing). Digest-Vorschau und
// Prognose beziehen sich nur auf den ERSTEN gewählten Plan; die Schaltflächen benennen ihn.

// #121 (Fund 2, Review): Rasterzeile aus einer oder zwei Karten. Die Basiskarte einer Zeile
// (Tester-Tabelle bzw. REP-5-Exit-Kriterien) stand vorher in ZWEI Ternary-Zweigen komplett
// ausgeschrieben — einmal im Zweispalten-Raster, einmal allein —, sodass ein neues Prop an beiden
// Stellen ergänzt werden musste. `Children.toArray` verwirft `null`/`undefined`/`false` selbst,
// jede Karte kann also als `{cond ? <X .../> : null}` genau einmal hineingeschrieben werden. Fehlt
// die zweite Karte (z. B. Blöcke ab Index 1, die weder simplCoverage noch chainCoverage bekommen),
// stünde in einem `lg:grid-cols-2` eine halbbreite Karte mit leerer zweiter Hälfte — deshalb fällt
// die Zeile dann auf die volle Breite zurück statt das Raster zu erzwingen.
function CardRow({ children }: { children: ReactNode }) {
  const items = Children.toArray(children);
  if (items.length === 0) return null;
  if (items.length === 1) return <>{items[0]}</>;
  return <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">{items}</div>;
}

function PlanReportBlock({
  plan,
  showHeading,
  scopeHint,
  summary,
  isSummaryLoading,
  isSummaryError,
  onRetrySummary,
  burndown,
  isBurndownError,
  forecast,
  trend,
  isTrendLoading,
  isTrendError,
  onRetryTrend,
  streams,
  isStreamsLoading,
  isStreamsError,
  onRetryStreams,
  testers,
  isTestersLoading,
  isTestersError,
  onRetryTesters,
  simplCoverage,
  chainCoverage,
  planName,
}: {
  plan: TestPlan;
  showHeading: boolean;
  // Task 7: Zuschnitt-Hinweis, aus dem Nutzer-Scope abgeleitet (scopeHintFrom). Gilt seitenweit
  // (nicht plan-abhängig) — wird hier nur durchgereicht an `TrendCard`, die als einzige Karte
  // ihre eigene Ausnahme benennen muss; die Begründung dazu steht dort an der Prop-Signatur.
  scopeHint: { node_ids: string[]; label: string | null } | null;
  summary: ReportSummary | undefined;
  isSummaryLoading: boolean;
  isSummaryError: boolean;
  onRetrySummary: () => void;
  burndown: BurndownReport | undefined;
  isBurndownError: boolean;
  forecast?: ForecastControls | undefined;
  trend: TrendResponse | undefined;
  isTrendLoading: boolean;
  isTrendError: boolean;
  onRetryTrend: () => void;
  // Task 11 (#98.2/#98.3): Stream-/Tester-Tabelle sind PLAN-gescopt (loadStreamsBreakdown/
  // loadTestersBreakdown rufen loadScope(projectId, planId, …) auf — dieselbe Grundmenge wie
  // REP-1/REP-4/REP-5/REP-6/Trend), deshalb je gewähltem Plan eine eigene Query, analog trend.
  streams: StreamRow[] | undefined;
  isStreamsLoading: boolean;
  isStreamsError: boolean;
  onRetryStreams: () => void;
  testers: TesterRow[] | undefined;
  isTestersLoading: boolean;
  isTestersError: boolean;
  onRetryTesters: () => void;
  // Task 4 (#121): projektweit, deshalb nur beim Block des ERSTEN gewaehlten Plans gesetzt
  // (ReportingPage reicht `index === 0 ? simplCoverage : undefined` durch) — s. Begruendung an
  // der Renderstelle unten.
  simplCoverage?: ReportSummary['simplificationCoverage'] | undefined;
  // Task 4 (#121): plan-gescopt, deshalb ebenfalls nur beim Block des ERSTEN gewaehlten Plans
  // gesetzt (`index === 0 ? chainCoverage : undefined`) — s. Begruendung an der Renderstelle unten.
  chainCoverage?: ReportSummary['e2eChainCoverage'] | undefined;
  // Review I5: Planname-Zusatz fuer die REP-6-Ueberschrift, nur bei mehreren gewaehlten Plaenen
  // gesetzt (showHeading) — s. Begruendung an der Renderstelle unten.
  planName?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const s = summary;

  // #38 (Finding 37): Die REP-2-Kachel zeigte nur die Gesamtsumme, die Aufschlüsselung stand als
  // eigene Sektion darunter — dadurch wirkte es, als würde nur eine Summe ausgegeben. Die Kachel
  // trägt die Aufschlüsselung jetzt selbst als Unterzeile ("2 Blocker · 3 Hoch"), die Balken
  // (SeverityBars) bleiben als visuelle Detailsicht darunter. Alle Severities mit offenen Defects
  // — inklusive der synthetischen "Ohne Severity"-Gruppe (`__none__`), die das Backend anhängt.
  // Bewusst NICHT herausgefiltert: dieselbe Menge wie in den Balken darunter, und nur so summiert
  // sich die Unterzeile zur Gesamtzahl der Kachel (`totalOpen` zählt herrenlose Defects mit).
  // Backend-Reihenfolge (rank, "Ohne Severity" zuletzt); Labels sprachabhängig aus den Buckets.
  const openSeverities = s ? s.severity.buckets.filter((b) => b.openCount > 0) : [];
  const severitySub =
    openSeverities.length > 0
      ? openSeverities
          .map((b) => `${b.openCount} ${pickLang(lang, b.label_de, b.label_en)}`)
          .join(' · ')
      : undefined;

  return (
    <div className="space-y-4">
      {/* Ab zwei gewählten Plänen bekommt jeder Block den Plan-Namen als Überschrift; bei genau
          einem gewählten Plan (showHeading=false) bleibt die Ansicht wie vor diesem Task. */}
      {showHeading ? (
        <h2 className="text-base font-bold text-text-primary">
          {t('reporting.planBlock', { name: plan.name })}
        </h2>
      ) : null}

      {/* #89: Schlug `report-summary` fehl, blieb `s` (summary) `undefined` — `{s ? … : null}`
          rendert dann NICHTS, eine leere Fläche ohne jede Erklärung. Kernsatz: ein Query-Fehler
          darf nie wie ein Leerzustand aussehen. Retry stößt gezielt summary UND burndown DIESES
          Plans neu an (onRetrySummary bündelt beide, s. ReportingPage unten). */}
      {isSummaryError ? <QueryErrorNotice onRetry={onRetrySummary} /> : null}

      {isSummaryLoading ? (
        <div className="text-sm text-text-muted">{t('common.loading')}</div>
      ) : null}

      {s ? (
        <>
          {/* Task 11: Grundmengen-Beschriftung je Karte. Zuordnung laut Global-Constraints-Block
              des Plans (Task 11): REP-1/Retest fällig sind PLAN-gescopt (loadScope(projectId,
              planId) — die Ausführungszahlen dieses Plans), REP-2/REP-3 sind PROJEKT-gescopt (die
              openDefectRows-Query in routes/reports.ts filtert nur nach project_id, ohne
              test_plan_id; die Coverage rechnet über den Conversion-Scope des Katalogs). Jede Karte
              trägt die Beschriftung als eigene kleine Zeile darunter — der Nenner ist Teil der
              Zahl, nicht nur Kommentar im Code. */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <KpiCard
                repLabel="REP-1"
                label={t('reporting.executed')}
                value={`${s.execution.executedPct}%`}
                sub={`${s.execution.byStatus.successful}✓ / ${s.execution.byStatus.error}✕`}
                tone="default"
              />
              <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
            </div>
            <div>
              <KpiCard
                repLabel="REP-1"
                label={t('reporting.passed')}
                value={`${s.execution.passedPct}%`}
                tone="pass"
              />
              <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
            </div>
            <div>
              <KpiCard
                repLabel="REP-2"
                label={t('reporting.rep2')}
                value={s.severity.totalOpen}
                sub={severitySub}
                hint={t('reporting.blockerHint')}
                tone={s.severity.blockerOnInScopeTotal > 0 ? 'fail' : 'default'}
                critical={s.severity.blockerOnInScopeTotal > 0}
              />
              <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopeProject')}</p>
            </div>
            <div>
              <KpiCard
                repLabel="REP-3"
                label={t('reporting.rep3')}
                value={`${s.coverage.coveragePct}%`}
                sub={t('reporting.coverageSub', {
                  covered: s.coverage.coveredProcesses,
                  total: s.coverage.inScopeProcesses,
                })}
                hint={t('reporting.coverageHint')}
                tone={s.coverage.coveragePct === 100 ? 'pass' : 'warn'}
              />
              <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopeProject')}</p>
            </div>
            {/* #92: dieselbe Regel wie die Retest-Sektion der Playlist (computeRetestDue) — in-scope
                Testfälle mit zuletzt error/blocked UND retest-bereitem Defect. Kein eigener
                REP-Bereich vorgesehen (requirements.md kennt REP-1..6 nicht dafür) — die Karte
                sitzt bewusst in derselben Kopf-Kachelreihe wie Ausführung/Coverage. */}
            <div>
              <KpiCard
                repLabel="REP-1"
                label={t('reporting.retestDue')}
                value={s.retestDueCount}
                hint={t('reporting.retestDueHint')}
                tone={s.retestDueCount > 0 ? 'warn' : 'default'}
              />
              <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
            </div>
            {/* Task 11: Aging-Karte — "Ältester offener Blocker" in Tagen. `oldestOpenBlockerDays`
                wird LIVE aus derselben project-weiten openDefects-Menge wie REP-2 gerechnet
                (routes/reports.ts, computeOldestOpenBlockerDays über severityDefects) — daher
                PROJEKT-gescopt, nicht Plan-gescopt. Kein REP-Kürzel: die Karte gehört zu keinem der
                REP-1..6-Bereiche aus requirements.md. `null` (kein offener Blocker) rendert GAR
                NICHT — kein Platzhalter, der Nichtwissen als "0 Tage" vortäuschen würde. */}
            {s.oldestOpenBlockerDays !== null ? (
              <div>
                <KpiCard
                  repLabel=""
                  label={t('reporting.agingLabel')}
                  value={t('reporting.agingDays', { count: s.oldestOpenBlockerDays })}
                  tone={s.oldestOpenBlockerDays > 7 ? 'warn' : 'default'}
                />
                <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopeProject')}</p>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ExecutionStatusCard execution={s.execution} />
            <SeverityCard severity={s.severity} />
          </div>

          {/* Task 4 (#121): REP-4 (Prognose) und der Wochenverlauf sitzen im selben
              Zweispalten-Raster wie REP-1/REP-2 oben — beide sind unabhängige Detailkarten ohne
              inhaltlichen Bezug zueinander, aber von ähnlicher Höhe; nebeneinander nutzt die
              Seitenbreite besser als zwei volle Zeilen darunter. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ForecastCard
              burndown={burndown}
              isBurndownError={isBurndownError}
              onRetrySummary={onRetrySummary}
              forecast={forecast}
            />

            <TrendCard
              scopeHint={scopeHint}
              trend={trend}
              isTrendLoading={isTrendLoading}
              isTrendError={isTrendError}
              onRetryTrend={onRetryTrend}
            />
          </div>

          <StreamsTableCard
            streams={streams}
            isStreamsLoading={isStreamsLoading}
            isStreamsError={isStreamsError}
            onRetryStreams={onRetryStreams}
          />
        </>
      ) : null}

      {/* #33 / Fund 1 (Review #121): Die Simplifications-Karte hängt an ihrer eigenen Datenquelle
          (`summaryQueries.find(q => q.data)?.data?.simplificationCoverage` in ReportingPage) —
          bewusst die ERSTE GELADENE Summary, egal welcher Plan, weil die Simplifications-Abdeckung
          projektweit identisch ist und deshalb nicht auf einen bestimmten Plan warten muss. Sie darf
          deshalb NICHT am `s`-Zustand DIESES Blocks hängen: hängt Plan A (Index 0) im Ladezustand
          oder schlägt fehl, während Plan B längst `simplificationCoverage` geliefert hat, muss die
          Karte trotzdem erscheinen — sonst steht sie nirgends auf der Seite (Repro: zwei gewählte
          Pläne, Block 0 lädt nie). Die Tester-Tabelle selbst bleibt an `s` gebunden (unverändert:
          keine Tabelle ohne Summary DIESES Blocks) — nur das Zweispalten-Raster mit der
          Simplifications-Karte liegt jetzt außerhalb des `s`-Zweigs. CardRow fällt auf volle Breite
          zurück, sobald nur eine der beiden Karten da ist, und liefert nichts, wenn keine da ist. */}
      <CardRow>
        {s ? (
          <TestersTableCard
            testers={testers}
            isTestersLoading={isTestersLoading}
            isTestersError={isTestersError}
            onRetryTesters={onRetryTesters}
          />
        ) : null}
        {simplCoverage ? <SimplificationCoverageCard simplCoverage={simplCoverage} /> : null}
      </CardRow>

      {s ? (
        // #69 (REP-6): E2E-Ketten-Abdeckung ("Tiefe") — eine EIGENE Sektion neben REP-5
        // (Exit-Kriterien) in diesem Plan-Block, strukturell wie die Simplifications-Sektion oben
        // neben der Tester-Tabelle, aber bewusst NICHT mit REP-3 (Coverage, "Breite") vermischt und
        // nicht mit ihr zusammengelegt. Bezieht sich (wie Prognose/Digest-Vorschau) auf den ERSTEN
        // gewaehlten Plan, weil der Plan-Scope (test_plan_items.in_scope) je Plan verschieden ist —
        // anders als die Simplifications, die projektweit identisch sind. ReportingPage reicht
        // `chainCoverage` deshalb nur beim Block des ERSTEN gewaehlten Plans durch; alle weiteren
        // Bloecke bekommen `undefined`. Anders als bei der Simplifications-Zeile ist hier kein
        // Sonderfall nötig: `chainCoverage` kommt aus `summaryQueries[0]`, ist also für DIESEN Block
        // genau dann gesetzt, wenn `s` (= summaryQueries[index]) für den Block mit index===0 gesetzt
        // ist — deshalb bleibt diese Zeile innerhalb des `s`-Zweigs.
        <CardRow>
          <ExitCriteriaCard exitCriteria={s.exitCriteria} />
          {chainCoverage ? (
            <div>
              <E2eChainCoverageSection
                data={chainCoverage}
                // Review I5: Bei mehreren gewaehlten Plaenen stand hier eine Prozentzahl OHNE
                // Plannamen, obwohl die Bloecke per showHeading ihren Plannamen tragen — die
                // Zahl war nicht zuzuordnen. Wie bei der Prognose wird der Planname deshalb
                // mitgegeben (nur dann, wenn es ueberhaupt mehrere Bloecke gibt).
                planName={planName}
              />
              <p className="mt-1 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
            </div>
          ) : null}
        </CardRow>
      ) : null}
    </div>
  );
}

export function ReportingPage() {
  const { t } = useTranslation();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;

  const plansQuery = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId!),
    enabled: Boolean(projectId),
  });
  const plans = plansQuery.data ?? [];
  // #114 (A9): „nicht aktiv" heisst jetzt Entwurf ODER abgeschlossen. Beide bleiben im Reporting
  // waehlbar — ein abgeschlossener Plan ist genau der Stand, den man spaeter noch auswerten will.
  const orderedPlans = [...sortActivePlans(plans), ...plans.filter((p) => p.status !== 'aktiv')];

  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  // #36: die Plan-Auswahl wird zusätzlich in der URL (`?plans=`, kommagetrennt) gespiegelt —
  // deep-linkbar, reload-fest, teilbar. Additiv über der bestehenden #55-Logik: der lokale State
  // bleibt die Render-Quelle und Schaltzentrale (Vorbelegung, Erhalt über Refetch, forecast/digest-
  // Reset), die URL ist nur die Persistenz-Schicht. `urlPlans` wird beim Laden als Deeplink gelesen
  // (nur existierende IDs gewinnen über die Default-Regel), Nutzer-Toggles schreiben über
  // `setUrlPlans` zurück.
  const [urlPlans, setUrlPlans] = useUrlStateList('plans');
  const plansIdsKey = plans.map((p) => p.test_plan_id).join(',');
  // Vorbelegung hängt an der ID-LISTE der Pläne, nicht am Array-OBJEKT `plans` — sonst würde jeder
  // Refetch (z. B. React-Query-Revalidierung) die Auswahl des Nutzers stillschweigend
  // überschreiben (dasselbe Muster wie signoff-page.tsx).
  // Die ID-Liste ändert sich aber sehr wohl, wenn jemand anderes einen Plan anlegt oder löscht —
  // beim nächsten Fensterfokus wird `plans` dann neu geladen. Eine vom Nutzer auf mehrere Pläne
  // erweiterte Auswahl darf dabei nicht verlorengehen: Es bleibt, was noch existiert; nur wenn
  // davon nichts übrig ist, greift wieder die Vorbelegung.
  // Branch-Review W3: dieser Effekt kann den ERSTEN gewählten Plan wechseln (z. B. wenn der
  // bisherige erste Plan verschwindet und ein anderer nachrückt) — Prognose/Digest-Vorschau hängen
  // aber am ERSTEN Plan (`firstPlanId`, s. u.) und müssen dann verworfen werden, sonst zeigt der
  // neu nachgerückte Block ein Ergebnis, das tatsächlich für den alten ersten Plan berechnet wurde.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusst nur die ID-Liste, s. o.
  useEffect(() => {
    setSelectedPlanIds((cur) => {
      const stillThere = cur.filter((id) => plans.some((p) => p.test_plan_id === id));
      let next = stillThere;
      if (next.length === 0) {
        // #36: ein gültiger `?plans=`-Deeplink (nur IDs, die es wirklich gibt) gewinnt über die
        // Default-Regel; unbekannte IDs werden ignoriert (nicht als Fehler behandelt) — additives
        // Muster wie in signoff-page.tsx. Nur wenn nichts Gültiges verlinkt ist, greift der Default.
        const fromUrl = urlPlans.filter((id) => plans.some((p) => p.test_plan_id === id));
        if (fromUrl.length > 0) {
          next = fromUrl;
        } else {
          const def = defaultActivePlanId(plans);
          next = def ? [def] : [];
        }
      }
      if ((next[0] ?? null) !== (cur[0] ?? null)) {
        forecastMutation.reset();
        digestPreviewMutation.reset();
      }
      return next;
    });
  }, [plansIdsKey]);

  function togglePlan(id: string) {
    // togglePlan läuft ausschließlich aus einem User-Event (Klick auf ein Kontrollkästchen) — dort
    // ist `selectedPlanIds` aus der Closure der aktuelle Wert. Deshalb wird `next` hier direkt
    // berechnet und NICHT im `setSelectedPlanIds`-Updater: der Updater muss rein sein (React führt
    // ihn während der Render-Phase aus, in StrictMode doppelt). Ein `setUrlPlans`/`setSearchParams`
    // darin löste reproduzierbar die Warnung „Cannot update a component (MemoryRouter) while
    // rendering a different component (ReportingPage)" aus (#36-Review). Seiteneffekte gehören
    // in den Event-Handler, nicht in den Updater.
    const cur = selectedPlanIds;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    // W3: ändert sich durch das Toggle der ERSTE Plan (z. B. Plan A abgewählt, Plan B rückt auf
    // Index 0), muss ein bereits geladenes Prognose-/Vorschau-Ergebnis verworfen werden — sonst
    // zeigt der B-Block ein Narrativ, das tatsächlich für Plan A berechnet wurde.
    if ((next[0] ?? null) !== (cur[0] ?? null)) {
      forecastMutation.reset();
      digestPreviewMutation.reset();
    }
    setSelectedPlanIds(next);
    // #36: die vom Nutzer geänderte Auswahl in die URL spiegeln (replace). Nur explizite Toggles
    // schreiben — die automatische Vorbelegung im Effekt oben lässt die URL bewusst sauber.
    setUrlPlans(next);
  }

  const summaryQueries = useQueries({
    queries: selectedPlanIds.map((id) => ({
      queryKey: ['report-summary', projectId, id],
      queryFn: () => reportsApi.summary(projectId!, id),
      enabled: Boolean(projectId),
    })),
  });
  const burndownQueries = useQueries({
    queries: selectedPlanIds.map((id) => ({
      queryKey: ['report-burndown', projectId, id],
      queryFn: () => reportsApi.burndown(projectId!, id),
      enabled: Boolean(projectId),
    })),
  });
  // #97: eigene Query je gewähltem Plan, analog burndownQueries — die Trend-Sektion hängt NICHT
  // an summary/burndown, weil sie einen eigenen Endpunkt hat (Task 6).
  const trendQueries = useQueries({
    queries: selectedPlanIds.map((id) => ({
      queryKey: ['report-trend', projectId, id],
      queryFn: () => reportsApi.trend(projectId!, id),
      enabled: Boolean(projectId),
    })),
  });
  // Task 11 (#98.2/#98.3): eigene Endpoints (Task 10), eigene Query je gewähltem Plan — analog
  // trendQueries. Beide sind PLAN-gescopt (loadStreamsBreakdown/loadTestersBreakdown rufen
  // loadScope(projectId, planId, …) auf), NICHT über /reports/summary?node_id gezogen.
  const streamsQueries = useQueries({
    queries: selectedPlanIds.map((id) => ({
      queryKey: ['report-streams', projectId, id],
      queryFn: () => reportsApi.streams(projectId!, id),
      enabled: Boolean(projectId),
    })),
  });
  const testersQueries = useQueries({
    queries: selectedPlanIds.map((id) => ({
      queryKey: ['report-testers', projectId, id],
      queryFn: () => reportsApi.testers(projectId!, id),
      enabled: Boolean(projectId),
    })),
  });

  // Task 7: Zuschnitt-Hinweis. `useScopedUser(projectId)` — nicht `useSession()` roh — sonst
  // urteilt die Zeile projektübergreifend (#23). Als Knotenliste dient die bereits geladene
  // Stream-Tabelle des ersten gewählten Plans (StreamRow trägt schon `node_id`/`name`) — die
  // Knotennamen selbst sind planunabhängig, eine zweite Abfrage wäre unnötig.
  const { user: scopedUser } = useScopedUser(projectId);
  const nodesForHint = streamsQueries[0]?.data?.streams ?? [];
  const scopeHint = scopeHintFrom(scopedUser, nodesForHint);

  const firstPlanId = selectedPlanIds[0] ?? null;
  const firstPlan = plans.find((p) => p.test_plan_id === firstPlanId) ?? null;

  // #33: Die Simplifications-Abdeckung ist projektweit gleich (Katalog gehoert dem Kunden, nicht
  // dem Plan) — das Backend liefert sie in jeder Summary identisch. Deshalb genau EINMAL pro Seite
  // aus der ersten geladenen Summary gezogen, nicht je Plan-Block wiederholt.
  const simplCoverage = summaryQueries.find((q) => q.data)?.data?.simplificationCoverage;

  // #69 (REP-6): E2E-Ketten-Abdeckung ist — anders als die Simplifications-Abdeckung — NICHT
  // projektweit gleich, sondern hängt am Plan-Scope (test_plan_items.in_scope). Deshalb bewusst
  // NICHT über "das erste geladene" Ergebnis gezogen (das könnte bei mehreren gewählten Plänen ein
  // beliebiger Plan sein), sondern fest an summaryQueries[0] gebunden — dieselbe "ERSTER gewählter
  // Plan"-Regel wie bei Prognose/Digest-Vorschau weiter unten (firstPlanId).
  const chainCoverage = summaryQueries[0]?.data?.e2eChainCoverage;

  const forecastMutation = useMutation({
    mutationFn: () => reportsApi.forecast(projectId!, firstPlanId!),
  });

  // Design-Spec 3.9: Kontextzeile-Aktionen „Digest-Vorschau" + „⭳ PDF" (NOT-5). Der PDF-Export
  // ist ein dokumentierter Fast-Follow (Welle 2) — der Chip ist bewusst present-but-disabled statt
  // ausgeblendet, mit Tooltip-Begründung. Beide Aktionen beziehen sich nur auf den ERSTEN
  // gewählten Plan (#55) — bei mehreren gewählten Plänen ist unklar, "wessen" Bericht/Prognose
  // gemeint wäre, und eine Aggregation über Pläne ist bewusst nicht vorgesehen.
  const digestPreviewMutation = useMutation({
    mutationFn: () => digestApi.preview(projectId!, firstPlanId!),
  });

  if (projectLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project) return <div className="p-6 text-text-muted">{t('reporting.noProject')}</div>;

  const showHeading = selectedPlanIds.length >= 2;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-text-primary">{t('reporting.title')}</h1>
        <div className="flex items-center gap-3">
          {plans.length === 0 ? (
            <span className="text-sm text-text-muted">{t('reporting.noPlan')}</span>
          ) : (
            <fieldset className="flex items-center gap-3 text-sm">
              <legend className="sr-only">{t('reporting.scope')}</legend>
              {orderedPlans.map((p) => (
                <label key={p.test_plan_id} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={selectedPlanIds.includes(p.test_plan_id)}
                    onChange={() => togglePlan(p.test_plan_id)}
                  />
                  <span className="text-text-body">{p.name}</span>
                </label>
              ))}
            </fieldset>
          )}
          <button
            type="button"
            onClick={() => digestPreviewMutation.mutate()}
            disabled={!firstPlanId}
            className="rounded-btn border border-brand-accent px-3 py-1 text-xs text-brand-accent disabled:opacity-50"
          >
            {t('digest.preview')}
            {firstPlan ? ` · ${firstPlan.name}` : ''}
          </button>
          <button
            type="button"
            disabled
            title={t('digest.pdfFastFollow')}
            className="rounded-chip border border-[var(--border)] px-2 py-1 text-xs text-text-muted opacity-50"
          >
            ⭳ PDF
          </button>
          <Link
            to="digest"
            className="rounded-btn border border-[var(--border)] px-3 py-1 text-xs text-text-body hover:text-brand-accent"
          >
            {/* #32: Linktext = Titel der Zielseite. Vorher stand hier die Abschnitts-Überschrift,
                sodass Einstieg und Seitentitel unterschiedlich hießen. */}
            {t('digest.title')}
          </Link>
        </div>
      </div>

      {/* Task 7: derselbe Zuschnitt, der Task 4-6 die Backend-Zahlen filtern lässt — ohne diesen
          Hinweis liest ein Verantwortlicher für "Vertrieb" den Fortschritt seines Teilprojekts als
          Projektstand (feature-durchstich, "Vollständigkeit"-Regel des Projekts).
          Fund 1 (Review): erscheint asynchron (die Namensquelle kann langsamer laden als die
          längst gefilterten Kennzahlen) und ist die einzige Warnung, dass die Zahlen beschnitten
          sind — deshalb aria-live="polite" statt eines stillen DOM-Updates. Ohne aufgelöste
          Knotennamen (scopeHint.label === null) greift der namenlose Rückfalltext (Fund 1) statt
          gar keinem Hinweis. */}
      {scopeHint ? (
        <p className="mb-3 text-sm text-text-muted" data-testid="scope-hint" aria-live="polite">
          {scopeHint.label !== null
            ? t('scope.hint', { nodes: scopeHint.label })
            : t('scope.hintFallback')}
        </p>
      ) : null}

      {digestPreviewMutation.data ? (
        <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            {/* W3: Plan-Name in der Überschrift, wie die auslösende Schaltfläche ihn schon trägt —
                sonst ist bei mehreren gewählten Plänen nicht erkennbar, wessen Vorschau hier steht. */}
            <h2 className="text-sm font-bold text-text-primary">
              {t('digest.preview')}
              {firstPlan ? ` · ${firstPlan.name}` : ''}
            </h2>
            <button
              type="button"
              onClick={() => digestPreviewMutation.reset()}
              aria-label={t('common.close')}
              className="text-xs text-text-muted hover:text-text-primary"
            >
              ✕
            </button>
          </div>
          {/* Fund I2 (Nachpruefung): `buildDigestData` kennt keinen Knoten-Zuschnitt — der
              Bericht ist immer projektweit. Ohne diese Zeile widerspricht die Vorschau direkt dem
              "Alle Zahlen auf dieser Seite"-Hinweis eine Zeile darueber. Nur sichtbar, wenn ein
              Zuschnitt aktiv ist (derselbe `scopeHint`, der auch die KPI-Kacheln beschriftet) —
              ein projektweiter Nutzer sieht diese Zeile nie. */}
          {scopeHint ? (
            <p className="mb-3 text-xs text-text-muted" data-testid="scope-hint-digest">
              {t('scope.hintDigest')}
            </p>
          ) : null}
          <iframe
            title={t('digest.preview')}
            srcDoc={digestPreviewMutation.data.html}
            className="h-[500px] w-full"
          />
        </section>
      ) : null}

      {/* K10 (Branch-Review): sind alle Pläne abgewählt, gab es hier bisher nur eine leere Fläche
          ohne Erklärung — der Nutzer konnte nicht unterscheiden, ob die Seite noch lädt oder ob die
          Abwahl schlicht nichts mehr zum Anzeigen übrig lässt. */}
      {plans.length > 0 && selectedPlanIds.length === 0 ? (
        <div className="text-sm text-text-muted">{t('reporting.noPlanSelected')}</div>
      ) : null}

      <div className="space-y-6">
        {selectedPlanIds.map((id, index) => {
          const plan = plans.find((p) => p.test_plan_id === id);
          if (!plan) return null;
          return (
            <PlanReportBlock
              key={id}
              plan={plan}
              showHeading={showHeading}
              scopeHint={scopeHint}
              summary={summaryQueries[index]?.data}
              isSummaryLoading={summaryQueries[index]?.isLoading ?? false}
              isSummaryError={summaryQueries[index]?.isError ?? false}
              onRetrySummary={() => {
                void summaryQueries[index]?.refetch();
                void burndownQueries[index]?.refetch();
              }}
              burndown={burndownQueries[index]?.data}
              isBurndownError={burndownQueries[index]?.isError ?? false}
              trend={trendQueries[index]?.data}
              isTrendLoading={trendQueries[index]?.isLoading ?? false}
              isTrendError={trendQueries[index]?.isError ?? false}
              onRetryTrend={() => void trendQueries[index]?.refetch()}
              streams={streamsQueries[index]?.data?.streams}
              isStreamsLoading={streamsQueries[index]?.isLoading ?? false}
              isStreamsError={streamsQueries[index]?.isError ?? false}
              onRetryStreams={() => void streamsQueries[index]?.refetch()}
              testers={testersQueries[index]?.data?.testers}
              isTestersLoading={testersQueries[index]?.isLoading ?? false}
              isTestersError={testersQueries[index]?.isError ?? false}
              onRetryTesters={() => void testersQueries[index]?.refetch()}
              forecast={
                index === 0
                  ? {
                      onCalculate: () => forecastMutation.mutate(),
                      narrative: forecastMutation.data?.narrative,
                      isSuccess: forecastMutation.isSuccess,
                      planName: plan.name,
                    }
                  : undefined
              }
              // #33: Simplifications-Abdeckung ist projektweit gleich (Katalog gehoert dem Kunden,
              // nicht dem Plan) — deshalb nur beim Block des ERSTEN gewaehlten Plans gesetzt, sonst
              // stuende sie mehrfach auf der Seite (Begruendung + Fallunterscheidung in
              // PlanReportBlock).
              simplCoverage={index === 0 ? simplCoverage : undefined}
              // #69 (REP-6): anders als die Simplifications haengt die E2E-Ketten-Abdeckung am
              // Plan-Scope — bewusst an summaryQueries[0]/firstPlanId gebunden (s. Berechnung
              // oben), deshalb ebenfalls nur beim Block des ERSTEN gewaehlten Plans gesetzt.
              chainCoverage={index === 0 ? chainCoverage : undefined}
              planName={
                index === 0 ? (showHeading ? (firstPlan?.name ?? undefined) : undefined) : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}
